export const DEFAULT_BOOKING_HOST_RULES_TEXT =
  'We are a very flexible venue but ask that you follow certain rules: Outside food catering is allowed, however, we are a licenced bar and you cannot bring in your own drinks. No smoking in the building Please keep noise down after...';

function hostRuleLineText(line) {
  if (line == null) return '';
  if (typeof line === 'string') return line.trim();
  if (typeof line === 'object') return String(line.name || line.label || '').trim();
  return String(line).trim();
}

export function isVenueAmenitiesObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function extractHostRulesFromEventRules(eventRules = {}) {
  const raw = eventRules.hostRules ?? eventRules.host_rules ?? [];
  return Array.isArray(raw) ? raw : [];
}

const BYO_HOST_RULE_PATTERN = /^BYO alcohol is (allowed|not allowed)\.?$/i;

/** Top-level venue fields win over nested amenities (matches venue dashboard). */
export function resolveVenueAlcoholSettings(venue) {
  if (!venue) {
    return { venueProvides: false, byoAllowed: false, corkageFee: false };
  }
  const rawAmenities = venue.amenities;
  const food =
    (isVenueAmenitiesObject(rawAmenities) &&
      (rawAmenities.food_beverage || rawAmenities.foodBeverage)) ||
    {};
  const nested = food.alcohol || {};
  return {
    venueProvides:
      venue.serves_alcohol != null
        ? Boolean(venue.serves_alcohol)
        : Boolean(nested.venueProvides),
    byoAllowed:
      venue.outside_alcohol_allowed != null
        ? Boolean(venue.outside_alcohol_allowed)
        : Boolean(nested.byoAllowed),
    corkageFee:
      venue.corkage_fee_for_byo != null
        ? Boolean(venue.corkage_fee_for_byo)
        : Boolean(nested.corkageFee),
  };
}

export function byoHostRuleLine(byoAllowed) {
  return byoAllowed ? 'BYO alcohol is allowed' : 'BYO alcohol is not allowed';
}

/** Replace stale BYO lines in host rules with copy derived from dashboard toggles. */
export function syncHostRulesWithAlcoholSettings(ruleLines, venue) {
  const filtered = (Array.isArray(ruleLines) ? ruleLines : [])
    .map(hostRuleLineText)
    .filter(Boolean)
    .filter((line) => !BYO_HOST_RULE_PATTERN.test(line));
  const { byoAllowed } = resolveVenueAlcoholSettings(venue);
  return [...filtered, byoHostRuleLine(byoAllowed)];
}

export function mergeAlcoholSettingsIntoAmenities(amenities, venue) {
  if (!isVenueAmenitiesObject(amenities)) return amenities;
  const alcohol = resolveVenueAlcoholSettings(venue);
  const nextFood = {
    ...(amenities.foodBeverage || amenities.food_beverage || {}),
    alcohol: {
      venueProvides: alcohol.venueProvides,
      byoAllowed: alcohol.byoAllowed,
      corkageFee: alcohol.corkageFee,
    },
  };
  return {
    ...amenities,
    foodBeverage: nextFood,
    food_beverage: nextFood,
  };
}

/** Normalize API / transformed amenities into a consistent object shape. */
export function normalizeVenueAmenitiesObject(raw) {
  if (!isVenueAmenitiesObject(raw)) return undefined;

  const rawEventRules = isVenueAmenitiesObject(raw.event_rules)
    ? raw.event_rules
    : isVenueAmenitiesObject(raw.eventRules)
      ? raw.eventRules
      : {};
  const hostRules = extractHostRulesFromEventRules(rawEventRules);
  const allowedEvents = rawEventRules.allowedEvents ?? rawEventRules.allowed_events ?? [];
  const cancellationPolicy =
    rawEventRules.cancellationPolicy ??
    rawEventRules.cancellation_policy ??
    '';

  const normalizedEventRules = {
    hostRules,
    host_rules: hostRules,
    allowedEvents: Array.isArray(allowedEvents) ? allowedEvents : [],
    cancellationPolicy,
    cancellation_policy: cancellationPolicy,
  };

  const rawFood = isVenueAmenitiesObject(raw.food_beverage)
    ? raw.food_beverage
    : isVenueAmenitiesObject(raw.foodBeverage)
      ? raw.foodBeverage
      : {};
  const normalizedFoodBeverage = {
    inHouseCatering: Array.isArray(rawFood.inHouseCatering) ? rawFood.inHouseCatering : [],
    externalCatering: Array.isArray(rawFood.externalCatering) ? rawFood.externalCatering : [],
    alcohol: {
      venueProvides: Boolean(rawFood.alcohol?.venueProvides),
      byoAllowed: Boolean(rawFood.alcohol?.byoAllowed),
      corkageFee: Boolean(rawFood.alcohol?.corkageFee),
    },
  };

  return {
    eventRules: normalizedEventRules,
    event_rules: normalizedEventRules,
    foodBeverage: normalizedFoodBeverage,
    food_beverage: normalizedFoodBeverage,
    amenities: Array.isArray(raw.amenities) ? raw.amenities : [],
  };
}

function getVenueAmenitiesContainer(venue) {
  const raw = venue?.amenities;
  if (isVenueAmenitiesObject(raw)) return normalizeVenueAmenitiesObject(raw) || {};
  return {};
}

function collectHostRuleLines(venue) {
  const amenities = getVenueAmenitiesContainer(venue);
  const eventRules = amenities.event_rules || amenities.eventRules || {};
  const fromEventRules = extractHostRulesFromEventRules(eventRules).map(hostRuleLineText).filter(Boolean);
  if (fromEventRules.length > 0) return fromEventRules;

  const topLevel = venue?.host_rules ?? venue?.hostRules;
  if (Array.isArray(topLevel)) {
    return topLevel.map(hostRuleLineText).filter(Boolean);
  }

  return [];
}

/** Host rules only — shown under "Review host rules" on the booking page. */
export function getBookingHostRulesText(venue) {
  const ruleLines = collectHostRuleLines(venue);
  if (ruleLines.length > 0) {
    return syncHostRulesWithAlcoholSettings(ruleLines, venue).join('\n');
  }
  return DEFAULT_BOOKING_HOST_RULES_TEXT;
}

/** Cancellation policy copy — separate from host rules in venue dash. */
export function getBookingCancellationPolicyText(venue) {
  const amenities = getVenueAmenitiesContainer(venue);
  const eventRules = amenities.event_rules || amenities.eventRules || {};
  const policy =
    eventRules.cancellationPolicy ||
    eventRules.cancellation_policy ||
    venue?.cancellation_policy ||
    '';
  return String(policy || '').trim();
}

/** Multiline form value for venue dashboard (one paragraph per line). */
export function hostRulesToFormText(hostRules) {
  if (!Array.isArray(hostRules) || hostRules.length === 0) {
    return '';
  }
  return hostRules.map(hostRuleLineText).filter(Boolean).join('\n');
}
